import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { forkJoin, Subscription, BehaviorSubject } from "rxjs";
import { Dictionary } from "src/app/store/models/common_types";
import {
  computeAverage,
  computeTotal,
  createCompsTable,
  createCompsTableFilter,
  createTimeseriesCompsTableRows,
  getSelectedMarketIdFromList,
  computeCompanyCount,
  getSelectedMarketItemsList,
} from "src/app/comps-page/utils";
import {
  CompsTableColumnData,
  CompsTable,
} from "src/app/comps-page/comps-page.model";
import { AppService } from "src/app/app.service";
import { getPeriodTypeDataId } from "src/app/utils/period-utils";
import {
  getTickerIdUrl,
  getTickersChunksForDataCall,
} from "src/app/utils/common";
import { CompsDataService } from "src/app/comps-page/comps-data.service";
import { NzMessageService } from "ng-zorro-antd/message";
import { environment } from "../../../environments/environment";

@Component({
  selector: "app-trend-grid",
  templateUrl: "./trend-grid.component.html",
  styleUrls: ["./trend-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrendGridComponent {
  @Input() isActive;
  @Input() industryId;
  @Input() lineItem;
  @Input() metaReady;
  @Input() currentIndex;
  @Input() periodList;
  @Input() showBlankRows;
  @Input() tickers;
  @Input() currency;
  @Input() viewBy;
  @Input() revision;
  @Input() periodData;
  @Input() periodLabel;
  @Input() magnitudeMessage;
  @Input() activePeriod;
  @Input() source;
  @Input() compType;
  @Input() compId;
  @Input() highlightBestMatches;
  @Input() fetchAndCreateCompsData;
  @Input() viewportOptions;
  @Input() showCompanyCount;
  @Input() marketItemsData;
  @Input() sortByColumnId;
  @Input() periodUserSetting;
  @Output() handleTickerOrderUpdate: EventEmitter<any> = new EventEmitter();
  @Output() OnDataCreationInitiate: EventEmitter<any> = new EventEmitter();
  @Output() OnCompsDataCallInPogressChange: EventEmitter<any> =
    new EventEmitter();
  @Output() onShowChart: EventEmitter<{
    item: Dictionary<any>;
    chartType: string;
  }> = new EventEmitter();

  allTickerInfo: Dictionary<any> = {};
  magnitude;
  unit;
  tickerPeriodEstimationData = {};
  compsTableData: CompsTable = {
    rows: [],
    columns: [],
    columnData: {},
    rowData: {},
    filter: {},
    extras: {},
    title: "",
  };
  lineHasTotal;
  lineItemsData;
  lineTotalMap = {};
  currencySymbol: string;
  toLoad;
  assets = environment.assetUrl;
  isAbs: boolean;

  isCompsDataInProgress;

  inViewportChange;

  // subscribers
  compsDataSubscriber: Subscription;
  totalLineItemLogicCheckSubscriber: Subscription;

  //observer
  compsGridData$: BehaviorSubject<any>;

  constructor(
    private ref: ChangeDetectorRef,
    private appService: AppService,
    private compsDataService: CompsDataService,
    private message: NzMessageService,
  ) {
    this.compsGridData$ = new BehaviorSubject<any>(null);
  }

  ngOnChanges(changes) {
    if (
      changes.fetchAndCreateCompsData &&
      changes.fetchAndCreateCompsData.currentValue !==
        changes.fetchAndCreateCompsData.previousValue
    ) {
      this.makeDataCalls();
    } else if (
      changes.showBlankRows &&
      changes.showBlankRows.currentValue !== changes.showBlankRows.previousValue
    ) {
      this.updateDataOnShowBlankSwitchChange(
        changes.showBlankRows.currentValue,
      );
    }
  }

  makeDataCalls() {
    if (
      this.lineItem === undefined ||
      !this.tickers ||
      this.tickers.length === 0
    ) {
      this.isCompsDataInProgress = true;
      const compsGridData = {
        rows: [],
        columns: [],
        columnData: {},
        rowData: {},
        filter: {},
        extras: {},
        title: "",
      };
      this.compsGridData$.next(compsGridData);
      this.ref.detectChanges();
      this.isCompsDataInProgress = false;
    } else {
      // make data calls
      this.createAndFetchCompTableData();
    }
  }

  createAndFetchCompTableData() {
    if (!this.lineItem) {
      return;
    }

    const dummyLineItemInfo: Dictionary<any> = {};
    dummyLineItemInfo[this.lineItem && this.lineItem.uniquesliid] = {
      magnitude: this.lineItem.magnitude || "M",
      unit: this.lineItem.unit || "Amount",
    };
    const periodType = getPeriodTypeDataId(this.periodData);

    const marketItems = getSelectedMarketItemsList(this.marketItemsData);
    this.compsTableData = createCompsTable(
      this.tickers.map((tickerInfo) => {
        return {
          full_name: tickerInfo.full_name,
          ticker: tickerInfo.ticker,
          sli_comps_id: tickerInfo.companyid,
        };
      }),
      {
        fuzzysli: 0,
        name: undefined,
        is_valuation_ratio: this.lineItem && this.lineItem.is_valuation_ratio,
        period: this.periodList,
        periodLabel: this.periodLabel,
        sli_comps_id: this.lineItem && this.lineItem.uniquesliid,
        sli_id: this.lineItem && this.lineItem.uniquesliid,
        source: this.source,
        addinName: this.lineItem.addin_name,
      },
      dummyLineItemInfo,
      this.magnitudeMessage,
      this.viewBy,
      this.revision,
      this.source,
      this.currency,
      this.lineHasTotal,
      this.periodData,
      marketItems,
    );

    this.lineHasTotal = this.lineItem && this.lineItem.totaled;

    this.compsTableData.filter = createCompsTableFilter(
      this.viewBy,
      this.revision,
      this.source,
      this.currency,
      this.lineHasTotal,
      undefined,
      this.periodData,
      true,
      false,
      true,
    );

    this.compsGridData$.next(this.compsTableData);
    this.ref.detectChanges();

    this.fetchAndHandleCompsData(
      this.viewBy,
      this.revision,
      this.getTickerIdArray(),
      this.periodList,
      this.lineItem && this.lineItem.uniquesliid,
      this.source,
      this.currency,
      periodType,
    );
  }

  fetchAndHandleCompsData(
    viewBy: Array<string>,
    revision: string,
    tickers: Array<string>,
    periods: Array<string>,
    uniquesliid: string,
    source: string,
    currency: string,
    periodType: string,
  ) {
    const prevPeriods = [];
    const otherPrevPeriods = [];
    this.isAbs = this.periodUserSetting["type"] === "abs";
    if (viewBy.includes("yoy")) {
      for (const period of periods) {
        if (otherPrevPeriods.length === 2 && prevPeriods.length === 4) {
          break;
        }
        let prevPeriod = "";
        if (period !== "LTM") {
          if (period === "NTM") {
            prevPeriod = "LTM";
            otherPrevPeriods.push(prevPeriod);
          } else if (period === "STM") {
            prevPeriod = "NTM";
            otherPrevPeriods.push(prevPeriod);
          } else {
            if (prevPeriods.length < 4) {
              if (this.isAbs) {
                const [basis, year] = period.toString().split("-");
                prevPeriod = `${basis}-${parseInt(year) - 1}`;
              } else {
                const basis = period.toString().replace(/[0-9-]/g, "");
                const year = period.toString().replace(/[a-zA-Z]/g, "");
                if (basis.includes("Q")) {
                  prevPeriod = basis + (parseInt(year) - 4);
                } else if (basis.includes("H")) {
                  prevPeriod = basis + (parseInt(year) - 2);
                } else {
                  prevPeriod = basis + (parseInt(year) - 1);
                }
              }
              prevPeriods.push(prevPeriod);
            }
          }
        }
      }
      periods = [...new Set([...periods, ...prevPeriods, ...otherPrevPeriods])];
    }
    this.isCompsDataInProgress = true;
    this.OnCompsDataCallInPogressChange.emit(true);

    const dataCalls: Array<Observable<any>> = [];

    let _viewBy = "viewBy=values";
    let _dataExistsCheck = "";
    if (viewBy.includes("values")) {
      _dataExistsCheck += `&data_exists_check=values`;
    }
    if (viewBy.includes("revision")) {
      _viewBy += `&viewBy=revision&revision=${revision}`;
      _dataExistsCheck += `&data_exists_check=revision`;
    } else if (viewBy.includes("yoy")) {
      _viewBy += `&viewBy=yoy`;
      _dataExistsCheck += `&data_exists_check=yoy`;
    }
    let currencyUrl = "";
    if (currency !== "Base") {
      currencyUrl += `&currency=${currency}`;
    }

    const tickersChunks = getTickersChunksForDataCall(tickers, 18);
    const industryParam = this.industryId
      ? `&industry_id=${this.industryId}`
      : "";

    const compTypeParam = this.compType ? `&comp_type=${this.compType}` : "";
    const compIdParam = this.compId ? `&id=${this.compId}` : "";
    let periodList = "";
    let otherPeriodList = "";
    for (const item of periods) {
      if (item === "NTM" || item === "LTM" || item === "STM") {
        otherPeriodList += `&other_period=${item}`;
      } else {
        periodList += `&period=${item}`;
      }
    }

    const marketItems = getSelectedMarketIdFromList(this.marketItemsData);

    for (let i = 0; i < tickersChunks.length; i++) {
      const tickerParams = getTickerIdUrl(tickersChunks[i]);
      let url = `${_viewBy}${_dataExistsCheck}${tickerParams}${industryParam}${currencyUrl}${compIdParam}${compTypeParam}&version=v2`;
      url = `${url}${periodList}${otherPeriodList}`;
      url = `${url}&period_type=${periodType}`;
      url = `${url}&uniquesliid=${uniquesliid}&source=${encodeURIComponent(
        source,
      )}`;

      if (marketItems && marketItems.length) {
        url += `&market_data=${marketItems.join("&market_data=")}`;
      }
      dataCalls.push(this.compsDataService.getCompsData(url));
    }

    if (this.compsDataSubscriber) {
      this.compsDataSubscriber.unsubscribe();
    }
    this.compsDataSubscriber = forkJoin(dataCalls).subscribe((reponseList) => {
      const lineInfo: Dictionary<any> = {};
      let currencySymbol = "";
      reponseList.forEach((res) => {
        if (res.status === "success") {
          Object.keys(res.data).forEach((ticker) => {
            this.allTickerInfo[ticker] = { ...res.data[ticker] };
            if (this.allTickerInfo[ticker]) {
              if (this.allTickerInfo[ticker][`${uniquesliid}|U`]) {
                lineInfo.unit = this.allTickerInfo[ticker][`${uniquesliid}|U`];
                this.unit = this.allTickerInfo[ticker][`${uniquesliid}|U`];
              }
              if (this.allTickerInfo[ticker][`${uniquesliid}|M`]) {
                lineInfo.magnitude =
                  this.allTickerInfo[ticker][`${uniquesliid}|M`];
                this.magnitude = this.allTickerInfo[ticker][`${uniquesliid}|M`];
              }
              if (this.allTickerInfo[ticker][`${uniquesliid}|C`]) {
                currencySymbol = this.allTickerInfo[ticker][`${uniquesliid}|C`];
              }
            }

            // estimated period handling
            if (res.data[ticker] && res.data[ticker][uniquesliid]) {
              this.tickerPeriodEstimationData[ticker] = [];
              for (let i = 0; i < this.periodList.length; i++) {
                const item = this.periodList[i];
                if (res.data[ticker][uniquesliid][item]) {
                  if (res.data[ticker][uniquesliid][item].is_forecast) {
                    this.tickerPeriodEstimationData[ticker].push(`${item}E`);
                  } else {
                    this.tickerPeriodEstimationData[ticker].push(item);
                  }
                }
              }
            }
          });
        }
      });
      this.updateLineInfo(this.magnitude, lineInfo.unit);

      const { rowData } = createTimeseriesCompsTableRows(
        this.compsTableData.rows,
        this.allTickerInfo,
        uniquesliid,
        periods,
        this.compsTableData.columnData[periods[0]].unit,
        marketItems,
      );

      this.compsTableData.rowData = rowData;

      // this.magnitude = lineInfo.magnitude;
      this.currencySymbol = currencySymbol;

      //update comps table data if remove blanks true , keep it at the end
      this.updateDataOnShowBlankSwitchChange(this.showBlankRows);
      this.isCompsDataInProgress = false;
      this.OnCompsDataCallInPogressChange.emit(false);
    });
  }

  updateLineInfo(magnitude: string, unit: string) {
    const newColumnData: Dictionary<CompsTableColumnData> = {};

    Object.keys(this.compsTableData.columnData).forEach((key: string) => {
      newColumnData[key] = {
        ...this.compsTableData.columnData[key],
        unit: unit,
        magnitude: magnitude,
      };
    });

    this.compsTableData = {
      ...this.compsTableData,
      columnData: newColumnData,
    };
  }

  getTickerIdArray() {
    return this.compsTableData && this.compsTableData.rows
      ? this.compsTableData.rows.map((item) => item.ticker)
      : [];
  }

  /**
   * It filters the compTableData on the basis of remove blanks switch value
   * If it is true it filters the tickers which have data for all periods
   * @param {boolean} value - elected value of remove blank
   */
  updateDataOnShowBlankSwitchChange(value) {
    this.isCompsDataInProgress = true;
    if (this.compsTableData.rows) {
      if (!value) {
        const currentLineItemId =
          this.lineItem && this.lineItem.uniquesliid
            ? this.lineItem.uniquesliid
            : "";
        const filterTickersWithBlankValues =
          this.compsTableData.rows.filter(
            (item) =>
              this.compsTableData &&
              this.compsTableData.rowData &&
              this.compsTableData.rowData[item.ticker] &&
              this.compsTableData.rowData[item.ticker][
                `${currentLineItemId}|data_exists`
              ],
          ) || [];
        this.compsTableData = {
          ...this.compsTableData,
          rows: filterTickersWithBlankValues,
        };
        this.compsTableData = this.appService.superClone(this.compsTableData);
      } else {
        this.compsTableData = {
          ...this.compsTableData,
          rows: this.getFormattedTickerInfo(this.tickers),
        };
      }

      computeTotal(
        this.compsTableData,
        true,
        "",
        this.currency,
        value,
        this.isAbs,
      );
      computeAverage(this.compsTableData, true, "", this.currency, value);
      computeCompanyCount(this.compsTableData, true, "", value);

      this.compsTableData = {
        ...this.compsTableData,
      };
    }
    this.compsGridData$.next(this.compsTableData);
    this.ref.detectChanges();
    this.isCompsDataInProgress = false;
  }

  getFormattedTickerInfo(tickers) {
    return tickers.map((tickerInfo) => {
      return {
        full_name: tickerInfo.full_name,
        ticker: tickerInfo.ticker,
        sli_comps_id: tickerInfo.companyid,
      };
    });
  }

  updateTickerOrder(tickerOrderedList) {
    this.handleTickerOrderUpdate.emit(tickerOrderedList);
  }

  onInViewportChange(isInViewport) {
    this.inViewportChange.next(isInViewport);
  }

  toggleFuzzySwitch() {
    if (
      this.highlightBestMatches &&
      this.compsTableData &&
      !this.isCompsDataInProgress
    ) {
      let hasFuzzyData = false;
      const fuzzyCols = this.compsTableData.columns.filter((colKey) => {
        return this.compsTableData.columnData[colKey].isFuzzy;
      });
      if (fuzzyCols.length) {
        hasFuzzyData = true;
      }
      if (!hasFuzzyData) {
        this.message.warning("No best match data for this peer analysis table");
      }
    }
  }

  showChartPopup(data) {
    this.onShowChart.emit(data);
  }
}
